import { Badge, Button, Group } from "@mantine/core"
import { useEffect, useMemo, useState } from "react"
import { formatNumber, formatPercent } from "atlas_forecasting_app/lib/format"
import { clamp01 } from "atlas_forecasting_app/lib/math"
import {
  approximateQuantileFromNumericForecast,
  buildDiscreteBuckets,
  discreteLabelForValue,
  multipleChoiceSemantics,
  quantileFromDiscrete,
} from "atlas_forecasting_app/forecasting/forecast_utils"
import { MultiSeriesHistoryLineChart } from "atlas_forecasting_app/components/charts/multi_series_history_line_chart"
import { HISTORY_LINE_COLORS, timeMsForForecast } from "atlas_forecasting_app/components/charts/history_utils"

function sampleDiscreteTickValues(values, maxTicks = 5) {
  const unique = Array.from(new Set(values.filter((value) => Number.isFinite(value)))).sort((a, b) => a - b)
  if (unique.length <= maxTicks) return unique

  return Array.from({ length: maxTicks }, (_, idx) => unique[Math.round((idx * (unique.length - 1)) / Math.max(1, maxTicks - 1))])
}

function ColorSwatch({ color }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: 999,
        background: color,
      }}
    />
  )
}

export function ForecastHistoryChart({
  question,
  forecasts,
  selectedForecastId,
  onSelectForecastId,
  compact = false,
  showLegend = true,
}) {
  const qType = question?.question_type

  const completed = useMemo(() => {
    return (forecasts || [])
      .filter((f) => f && f.status === "completed" && f.forecast_data != null)
      .map((f) => ({ ...f, _ms: timeMsForForecast(f) }))
      .filter((f) => f._ms != null)
      .sort((a, b) => a._ms - b._ms)
  }, [forecasts])

  const optionLabels = useMemo(() => {
    if (qType !== "multiple_choice") return []
    const opts = Array.isArray(question?.options) ? question.options.map((o) => String(o)) : []
    const fromForecasts = new Set()
    for (const f of completed) {
      const data = f.forecast_data
      if (!data || typeof data !== "object") continue
      for (const k of Object.keys(data)) fromForecasts.add(String(k))
    }
    if (opts.length === 0) return Array.from(fromForecasts).sort((a, b) => a.localeCompare(b))
    const seen = new Set(opts)
    for (const k of fromForecasts) {
      if (!seen.has(k)) opts.push(k)
    }
    return opts
  }, [qType, question?.options, completed])

  const colorByOption = useMemo(() => {
    const map = new Map()
    for (let i = 0; i < optionLabels.length; i += 1) {
      const label = optionLabels[i]
      map.set(label, HISTORY_LINE_COLORS[i % HISTORY_LINE_COLORS.length])
    }
    return map
  }, [optionLabels.join("|")])

  const maxSeries = compact ? 4 : 7

  const defaultVisibleOptions = useMemo(() => {
    if (qType !== "multiple_choice") return []
    if (optionLabels.length <= maxSeries) return optionLabels
    const latest = completed[completed.length - 1]
    const data = latest?.forecast_data && typeof latest.forecast_data === "object" ? latest.forecast_data : {}
    const ranked = optionLabels
      .map((label) => [label, clamp01(Number(data[label])) ?? 0])
      .sort((a, b) => b[1] - a[1])
      .slice(0, maxSeries)
      .map(([label]) => label)
    return ranked.length ? ranked : optionLabels.slice(0, maxSeries)
  }, [qType, optionLabels, completed, maxSeries])

  const [visibleOptions, setVisibleOptions] = useState([])

  useEffect(() => {
    if (qType !== "multiple_choice") {
      setVisibleOptions([])
      return
    }
    setVisibleOptions(defaultVisibleOptions)
  }, [qType, defaultVisibleOptions.join("|")])

  if (completed.length === 0) return <Badge variant="light">No completed forecasts yet.</Badge>

  if (qType === "binary") {
    const points = completed.map((f) => ({
      x: f._ms,
      y: clamp01(Number(f.forecast_data)),
      forecastId: f.id,
    }))
    const series = [
      {
        key: "p_yes",
        label: "P(Yes)",
        color: HISTORY_LINE_COLORS[0],
        points,
      },
    ]
    const chartHeight = compact ? 200 : 240
    return (
      <MultiSeriesHistoryLineChart
        series={series}
        height={chartHeight}
        yDomain={[0, 1]}
        yTicks={[0, 0.25, 0.5, 0.75, 1]}
        yTickFormatter={(v) => formatPercent(v, 0)}
        pointFormatter={(v) => `P(Yes) ${formatPercent(v, 2)}`}
        selectedForecastId={selectedForecastId}
        onSelectForecastId={onSelectForecastId}
      />
    )
  }

  if (qType === "multiple_choice") {
    const semantics = multipleChoiceSemantics(question)
    const activeOptions = visibleOptions.length ? visibleOptions : optionLabels

    const series = activeOptions.map((label, idx) => {
      const color = colorByOption.get(label) || HISTORY_LINE_COLORS[idx % HISTORY_LINE_COLORS.length]
      const points = completed.map((f) => {
        const data = f.forecast_data
        const y = data && typeof data === "object" ? clamp01(Number(data[label])) : null
        return { x: f._ms, y, forecastId: f.id }
      })
      return { key: `opt_${label}`, label, color, points }
    })

    const toggle = (label) => {
      setVisibleOptions((prev) => {
        const next = prev.includes(label) ? prev.filter((l) => l !== label) : prev.concat([label])
        return next.length === 0 ? defaultVisibleOptions : next
      })
    }

    const chartHeight = compact ? 220 : 260

    return (
      <>
        <MultiSeriesHistoryLineChart
          series={series}
          height={chartHeight}
          yDomain={[0, 1]}
          yTicks={[0, 0.25, 0.5, 0.75, 1]}
          yTickFormatter={(v) => formatPercent(v, 0)}
          pointFormatter={(v) => `${semantics.valueLabel} ${formatPercent(v, 2)}`}
          selectedForecastId={selectedForecastId}
          onSelectForecastId={onSelectForecastId}
        />
        {semantics.note ? (
          <Badge variant="transparent" c="dimmed" px={0} mt="sm">
            {semantics.note}
          </Badge>
        ) : null}
        {optionLabels.length > 0 && showLegend ? (
          <Group gap="xs" mt="sm">
            {optionLabels.map((label) => {
              const color = colorByOption.get(label) || HISTORY_LINE_COLORS[0]
              const active = activeOptions.includes(label)

              return (
                <Button
                  key={label}
                  type="button"
                  size="compact-sm"
                  variant={active ? "light" : "subtle"}
                  color={active ? "blue" : "gray"}
                  leftSection={<ColorSwatch color={color} />}
                  onClick={() => toggle(label)}
                >
                  {label}
                </Button>
              )
            })}
          </Group>
        ) : null}
        {!showLegend && activeOptions.length > 0 ? (
          <Group gap="xs" mt="sm">
            {activeOptions.map((label) => {
              const color = colorByOption.get(label) || HISTORY_LINE_COLORS[0]
              return (
                <Badge key={label} variant="light" leftSection={<ColorSwatch color={color} />}>
                  {label}
                </Badge>
              )
            })}
          </Group>
        ) : null}
      </>
    )
  }

  if (qType === "discrete" || qType === "numeric") {
    const percentileDefs = [
      { key: "p05", label: "5%", q: 0.05, color: "rgba(255,95,109,0.90)", strokeWidth: 2.0 },
      { key: "p25", label: "25%", q: 0.25, color: "rgba(196,132,255,0.90)", strokeWidth: 2.0 },
      { key: "p50", label: "50%", q: 0.5, color: "rgba(75,211,123,0.92)", strokeWidth: 2.6 },
      { key: "p75", label: "75%", q: 0.75, color: "rgba(121,166,255,0.92)", strokeWidth: 2.0 },
      { key: "p95", label: "95%", q: 0.95, color: "rgba(255,171,64,0.90)", strokeWidth: 2.0 },
    ]

    const series = percentileDefs.map((def) => {
      const points = completed.map((f) => {
        if (qType === "discrete") {
          const buckets = buildDiscreteBuckets(question, f.forecast_data)
          const bucket = quantileFromDiscrete(buckets, def.q)
          const y = bucket?.plotValue ?? null
          return { x: f._ms, y, forecastId: f.id }
        }
        const y = approximateQuantileFromNumericForecast(f.forecast_data, def.q, question)
        return { x: f._ms, y, forecastId: f.id }
      })
      return { key: def.key, label: def.label, color: def.color, strokeWidth: def.strokeWidth, points }
    })

    const isDiscrete = qType === "discrete"
    const discreteYTicks = isDiscrete ? sampleDiscreteTickValues(series.flatMap((entry) => entry.points.map((point) => point.y)), compact ? 4 : 5) : null

    const chartHeight = compact ? 220 : 260

    return (
      <>
        <MultiSeriesHistoryLineChart
          series={series}
          height={chartHeight}
          yTicks={discreteYTicks}
          yTickFormatter={(v) => (isDiscrete ? discreteLabelForValue(question, v) : formatNumber(v, 2))}
          pointFormatter={(v) => `Value ${isDiscrete ? discreteLabelForValue(question, v) : formatNumber(v, 2)}`}
          selectedForecastId={selectedForecastId}
          onSelectForecastId={onSelectForecastId}
        />
        <Group gap="xs" mt="sm">
          <Badge variant="transparent" c="dimmed" px={0}>
            Percentiles
          </Badge>
          {percentileDefs.map((d) => (
            <Badge key={d.key} variant="light" leftSection={<ColorSwatch color={d.color} />}>
              {d.label}
            </Badge>
          ))}
        </Group>
      </>
    )
  }

  return null
}
